import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type { RightsIssue } from "@shared/content-rights";
import type { ImageIssue } from "@shared/vod-images";
import type { LocalisedIssue } from "@shared/vod-localised";
import type { DurationIssue, YearIssue } from "@shared/vod-title";
import type { CarouselIssue } from "@shared/carousel";
import type { PackageFinding } from "@shared/ingest-package";
import type {
  CatalogueProblem,
  CatalogueProblemCode,
} from "@shared/catalogue-health";

/**
 * The coded verdicts, in words (`BE-15`).
 *
 * Every one of these is DERIVED - by `assetPlaybackBlockers`, `rightsIssues`,
 * `imageIssues`, `localisedIssues`, `yearIssues`, `durationIssues` - and none of
 * them is stored anywhere. This module is the only place that turns a code into
 * a sentence, so a list row and the dialog that edits it cannot describe the
 * same finding two different ways.
 *
 * A code with no case here still SHOWS - as its own code - rather than
 * disappearing. A finding that vanishes because nobody wrote its copy is the
 * same failure as a field silently dropped on save: the row looks fine and is
 * not.
 */

export function blockerText(blocker: AssetPlaybackBlocker): string {
  switch (blocker.code) {
    case "no_playable_media":
      return "No playable media. A trailer or a clip is not something a guest can select.";
    case "media_has_no_resources":
      return `Media ${blocker.mediaIndex + 1}${
        blocker.mediaName ? ` (${blocker.mediaName})` : ""
      } has no stream URLs.`;
    case "resource_url_invalid":
      return `Media ${blocker.mediaIndex + 1}, stream ${
        blocker.resourceIndex + 1
      }: “${blocker.url}” is not an absolute URL naming a host.`;
    case "resource_protocol_unknown":
      return `Media ${blocker.mediaIndex + 1}, stream ${
        blocker.resourceIndex + 1
      }: protocol “other” — the catalogue will not say what it is and no player can guess.`;
    case "encrypted_without_drm_content_id":
      return "Encrypted, but the asset carries no DRM content id — production mints no licence and the stream plays in the clear.";
    case "encrypted_without_dash_resource":
      return "Encrypted, but nothing encrypted is DASH — licence minting is gated on encrypted DASH, so this plays in the clear.";
    case "drm_content_id_unverified":
      return "Cannot tell whether the DRM content id is present: this draft has not been saved and read back.";
    default:
      return (blocker as { code: string }).code;
  }
}

/** A content-health filter chip: what the count next to it is a count OF. */
export function catalogueProblemLabel(code: CatalogueProblemCode): string {
  switch (code) {
    case "title_no_media":
      return "Titles with no media";
    case "title_not_playable":
      return "Titles not playable";
    case "channel_not_playable":
      return "Channels not playable";
    case "channel_no_epg":
      return "Channels without EPG";
    case "channel_no_artwork":
      return "Channels without artwork";
    case "channel_no_localised_text":
      return "Channels without name or description";
    case "channel_untitled_language":
      return "Channels missing a name";
    case "channel_no_description":
      return "Channels without description";
    default:
      return String(code);
  }
}

/** One content-health problem on one row, in words. */
export function catalogueProblemText(problem: CatalogueProblem): string {
  switch (problem.code) {
    case "title_no_media":
      return "No media - there is nothing a guest can play.";
    case "title_not_playable":
    case "channel_not_playable":
      // The first blocker is the one to fix first; the editor lists the rest.
      return problem.blockers?.[0]
        ? blockerText(problem.blockers[0])
        : "Cannot be played.";
    case "channel_no_epg":
      return "Not mapped to a programme guide, so the guide has nothing to show.";
    case "channel_no_artwork":
      return "No logo or artwork at all.";
    case "channel_no_localised_text":
      return "No name or description in any language.";
    case "channel_untitled_language":
      return `No name in ${(problem.languages ?? []).join(", ") || "one language"}.`;
    case "channel_no_description":
      return "No description in any language.";
    default:
      return (problem as { code: string }).code;
  }
}

export function rightsIssueText(issue: RightsIssue): string {
  switch (issue.code) {
    case "encrypted_flag_disagrees_with_media":
      return "The licence's `encrypted` flag disagrees with the streams. One of them is wrong and the catalogue will not say which.";
    case "no_market_available_now":
      return "No market is licensed right now.";
    case "expires_within_30_days":
      return "A licence window closes within 30 days.";
    default:
      return issue.code;
  }
}

export function imageIssueText(issue: ImageIssue): string {
  switch (issue.kind) {
    case "no_images":
      return "No artwork at all.";
    case "unstorable_language":
      return `Artwork language “${issue.language}” cannot be stored: the column is 2–3 lowercase letters with no region.`;
    case "unknown_type":
      return `Artwork in ${issue.language} has an unknown type “${issue.type}”.`;
    case "empty_url":
      return `An ${issue.type} in ${issue.language} has no URL.`;
    case "absolute_url":
      return `An ${issue.type} in ${issue.language} stores an absolute URL, so it cannot go through the scaler.`;
    case "unmeasured_image":
      return `An ${issue.type} in ${issue.language} has no dimensions recorded.`;
    case "undelivered_language":
      return `Artwork exists in ${issue.language} but no localised text does, so a device never asks for it.`;
    case "unreachable_images":
      return `${issue.count} image(s) in ${issue.language} are never delivered.`;
    default:
      return (issue as { kind: string }).kind;
  }
}

export function localisedIssueText(issue: LocalisedIssue): string {
  switch (issue.kind) {
    case "no_localised_text":
      return "No localised text in any language.";
    case "blank_language":
      return `${issue.language} has a row with nothing in it.`;
    case "unstorable_language":
      return `Language “${issue.language}” cannot be stored: the column is 2–3 lowercase letters with no region.`;
    case "incomplete_language":
      return `${issue.language} is missing ${issue.missing.join(", ")}.`;
    default:
      return (issue as { kind: string }).kind;
  }
}

export function yearIssueText(issue: YearIssue): string {
  switch (issue.code) {
    case "year_only_in_release_year":
      return `The year is only in \`releaseYear\` (${issue.releaseYear}), which nothing reads — a device shows 0.`;
    case "years_disagree":
      return `Two years disagree: production ${issue.productionYear}, release ${issue.releaseYear}. Only production reaches a device.`;
    default:
      return (issue as { code: string }).code;
  }
}

export function durationIssueText(issue: DurationIssue): string {
  switch (issue.code) {
    case "duration_may_be_minutes":
      return `Length ${issue.assetSeconds} looks like MINUTES beside a media of ${Math.round(
        issue.mediaSeconds / 60
      )} min. The field is seconds.`;
    case "duration_disagrees_with_media":
      return `Length ${issue.assetSeconds}s disagrees with the media's ${issue.mediaSeconds}s.`;
    case "duration_missing":
      return `No length recorded, though a media runs ${Math.round(
        issue.mediaSeconds / 60
      )} min.`;
    default:
      return (issue as { code: string }).code;
  }
}

/**
 * The server's coded refusals, in words.
 *
 * Only the ones a person can act on from inside a dialog. Anything else falls
 * through to the code itself, which is still better than "something went wrong":
 * the code is greppable and the hint travels with it in the response body.
 */
/**
 * A carousel's findings, in words (`BE-28`).
 *
 * Every one describes a row the portal can already produce, so none of them
 * refuses a save — they are reported. That is the deviation rule applied to the
 * UI: a copied row that arrives with one must still be editable.
 */
export function carouselFindingText(issue: CarouselIssue): string {
  switch (issue.code) {
    case "name_missing":
      return "A name is required — the id is derived from it, so a nameless carousel has no id.";
    case "title_missing":
      return "No titles yet. Without one the rail draws its internal name on the television.";
    case "title_blank_text":
      return issue.subject
        ? `The ${issue.subject.toUpperCase()} title is empty. A blank row still reaches the device.`
        : "A title row has no text. A blank row still reaches the device.";
    case "title_duplicate_language":
      return `${issue.subject?.toUpperCase() ?? "That language"} is titled twice. The device takes one of them and there is no rule saying which.`;
    case "curated_no_assets":
      return "A manual carousel with no media picked draws nothing at all.";
    case "query_no_filters":
      return "A dynamic collection with no tag, type or source filter matches the whole catalogue — including every catch-up recording.";
    case "epg_no_channel":
      return "An EPG rail needs a channel. Without one the app has no schedule to draw.";
    case "epg_assets_ignored":
      return "Picked media is ignored on an EPG rail — the app draws the schedule itself. The picks are kept, not deleted, but they will never appear.";
    case "type_not_rendered":
      return `“${issue.subject}” is not rendered on this stack. The row is kept exactly as it is; it simply never reaches a television.`;
    case "display_limit_not_positive":
      return "A display limit of zero or less is treated as unset, and the rail falls back to 10.";
    default:
      return (issue as { code: string }).code;
  }
}

/**
 * A package finding, in words (`BE-31`).
 *
 * The mapper emits CODES rather than prose precisely so this can exist once:
 * the portal writes free-text warnings, which cannot be counted, filtered, or
 * tested without asserting on English. An unknown code still shows AS its code
 * — a finding that vanished because nobody wrote its sentence is a package that
 * looks clean and is not.
 */
export function packageFindingText(finding: PackageFinding): string {
  const ctx = (finding.context ?? {}) as Record<string, unknown>;
  const value = (key: string): string => String(ctx[key] ?? "");

  switch (finding.code) {
    case "package_empty":
      return "Nothing has been uploaded yet, or every file in the folder was ignored.";
    case "no_video_files":
      return "No MP4 anywhere in the package. There is nothing to transcode.";
    case "mixed_layout":
      return "Videos were found both in folders and at the root. The folders win, and the root videos are ignored rather than guessed at.";
    case "folder_without_video":
      return "This folder has no video in it, so it becomes no title.";
    case "folder_with_several_videos":
      return `Several videos in one folder. The first (${value("using")}) is used; the others are not.`;
    case "unpaired_file":
      return "Nothing claimed this file. A sidecar or an image only pairs with a video of the same name.";
    case "global_metadata_unreadable":
      return "The package-wide metadata.json could not be read, so its defaults were not applied to anything.";
    case "metadata_unreadable":
      return "This item's metadata.json could not be read. The title falls back to the filename.";
    case "metadata_not_an_object":
      return "This metadata.json is not a JSON object, so none of it could be used.";
    case "unknown_asset_type":
      return `“${value("type")}” is not a type the catalogue has. Allowed: ${String(ctx.allowed ?? "")}.`;
    case "source_type_not_allowed":
      return "A package only creates on-demand titles. The stated source type is not one of them.";
    case "unknown_image_type":
      return `“${value("type")}” is not an image type the catalogue has.`;
    case "invalid_code":
      return `“${value("value")}” is not a two- or three-letter code. The catalogue's column cannot hold a region form like pt-BR, and truncating it would write a code matching nothing.`;
    case "invalid_duration":
      return "The duration is not a number of seconds.";
    case "title_too_long":
      return `The title is ${value("length")} characters and the catalogue holds ${value("limit")}. Nothing is truncated — a silently shortened title is a wrong title that looks deliberate.`;
    case "duplicate_localization_language":
      return "Two localisations claim the same language. The catalogue keys on it, so one would silently overwrite the other.";
    case "blank_localization_field":
      return "A localisation field is blank. A blank row still reaches the device.";
    case "image_file_missing":
      return "The metadata names an image that is not in the package.";
    case "subtitle_file_missing":
      return "The metadata names a subtitle file that is not in the package.";
    case "subtitle_language_required":
      return "A subtitle with no language cannot be filed.";
    case "localization_language_required":
      return "A localisation with no language cannot be stored — the language IS its key.";
    case "unknown_provider":
      return `No provider called “${value("provider")}” exists, and one is never created automatically: a typo would mint a permanent shelf in a catalogue that cannot delete one.`;
    case "ambiguous_provider":
      return `“${value("provider")}” matches more than one provider. Naming the id settles it; guessing would file the title under the wrong distributor.`;
    case "unknown_entitlement":
      return `No package resolves for “${value("entitlement")}”. Ingesting against it would leave the title transcoded, delivered, and invisible to every guest.`;
    case "provider_from_account":
      return `Filed under “${value("providerId")}”, the supplier this account delivers for. A package cannot choose its own distributor — that is what the supplier agreement decides.`;
    case "packages_decided_by_screening":
      return "A package cannot put itself on a shelf. Which packages carry these titles is decided when Nenda screens them, so the `entitlements` in this metadata were ignored.";
    case "supplier_not_bound":
      return "This account is not linked to a supplier yet, so there is nothing to attribute the upload to. Nenda has to make that link before the package can be started.";
    case "subtitles_are_labels_upstream":
      return "The catalogue stores subtitle LABELS, not files. These are carried for delivery; nothing here attaches them to the title.";
    default:
      return (finding as { code: string }).code;
  }
}

export function saveErrorText(code: string | undefined, fallback: string) {
  switch (code) {
    case "external_id_required":
      return "The distributor's own id is required. Foyer does not mint one — `externalId` is not unique upstream, so a value we invented would look like a key and would not be one.";
    case "original_title_required":
      return "A name is required. A catalogue quietly filling with blank rows is harder to undo than a refusal.";
    case "unknown_provider":
      return "That provider does not exist in the catalogue.";
    case "content_api_unauthorized":
      return "Foyer's Content API credential was refused. That is a server configuration fault, not a problem with your account.";
    case "content_api_unreachable":
      return "The catalogue is unreachable. Nothing was saved — and an empty list would have been a lie.";
    case "content_api_error":
      return "The catalogue answered with an error. Nothing was saved.";
    case "content_api_rejected":
      return "The catalogue rejected this payload.";
    case "collection_would_be_emptied":
      return "This save would delete every row in a collection.";
    case "children_would_be_orphaned":
      return "This series still has seasons or episodes. Deleting it would leave them pointing at nothing, still costing a licence.";
    case "carousel_exists":
      return "A carousel with that name already exists. Upstream would have silently overwritten it — and re-pointed every room type showing it at different content.";
    case "name_not_sluggable":
      return "That name contains no characters an id can be built from.";
    case "carousel_in_use":
      return "This carousel is still shown by at least one room type or sub-page.";
    case "sub_page_in_use":
      return "This page is still listed by at least one room type.";
    case "name_required":
      return "A name is required. A catalogue quietly filling with blank rows is harder to undo than a refusal.";
    case "channel_in_use":
      return "This channel is still carried by at least one location.";
    case "vod_in_use":
      return "This title is still offered by at least one location.";
    case "type_not_editable":
      return "A row's type is fixed at creation upstream.";
    case "sort_set_too_large":
      return "That order needs every matching row read first, and there are too many. Narrow the filter and the same order becomes answerable.";
    case "search_too_short":
      return "Search needs at least three characters.";
    default:
      return fallback;
  }
}

/**
 * A guide read's refusal, in words (`BE-27`).
 *
 * The EPG routes are the one place in Distribution where a refusal arrives with
 * a `detail` **already written for the person reading it** - the window came out
 * of a query string, so `epg_window_inverted` says which way round the two
 * instants are and `epg_too_many_channels` says what the cap is and why. That
 * sentence is rendered as it came rather than paraphrased here, because a second
 * wording in this file is a second wording that can drift from the rule actually
 * enforcing it.
 *
 * Everything else - `content_api_unreachable`, `content_api_error` - is the same
 * refusal the lists already word, so it goes through {@link saveErrorText}. That
 * matters more here than anywhere: an unreachable catalogue must read as "the
 * catalogue is down", never as "this channel has no guide".
 */
export function epgErrorText(error: {
  code?: string;
  detail?: string;
  message: string;
}): string {
  const detail = typeof error.detail === "string" ? error.detail.trim() : "";
  if (error.code?.startsWith("epg_") && detail) return detail;
  return saveErrorText(error.code, error.message);
}
